// src/hooks/useNexusQuery.js

import { useState, useEffect, useMemo, useRef } from 'react';
import useAuthenticatedPost from './useAuthenticatedPost';
import config from '../config';

/**
 * Runs a stored NexusQuery by its QueryKey and returns the result rows.
 *
 * The point of this hook is that no SQL lives in the frontend. Queries are registered as rows in
 * `Data.NexusQueries`, so a BI analyst can read, validate and edit them in Query Commander
 * (/query-commander/editor?queryKey=...) without touching React or C#. This hook is just the
 * transport.
 *
 * Backed by `POST /queries/execute?queryKey={key}` (QueryCommanderController), which returns a
 * QueryExecutionResultDto: { success, rowCount, data: [{col: val}], errors: [] }. Note that the API
 * reports failures as HTTP 200 with `success: false`, so the error path is checked on the body
 * rather than on the HTTP status.
 *
 * @param {string|null} queryKey  Stored query key. Pass null to skip execution.
 * @param {Object} [args]         Argument values substituted into the SQL's {name} placeholders.
 * @returns {{ rows: Array, isLoading: boolean, error: Error|null, rowCount: number, refetch: Function }}
 */
export default function useNexusQuery(queryKey, args) {
  const { post } = useAuthenticatedPost();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(Boolean(queryKey));
  const [error, setError] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  // Serialize args so a caller passing an object literal inline doesn't re-fire the effect on
  // every render.
  const argsKey = useMemo(() => JSON.stringify(args ?? {}), [args]);

  // `post` is recreated on each render by useAuthenticatedPost, so it can't go in the dep array
  // without causing an infinite loop.
  const postRef = useRef(post);
  postRef.current = post;

  useEffect(() => {
    if (!queryKey) {
      setRows([]);
      setRowCount(0);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    postRef
      .current(
        `${config.apiBaseUrl}/queries/execute?queryKey=${encodeURIComponent(queryKey)}`,
        JSON.parse(argsKey)
      )
      .then((result) => {
        if (cancelled) return;
        if (result && result.success === false) {
          const message = result.errors?.length
            ? result.errors.join('; ')
            : `Query "${queryKey}" failed.`;
          setError(new Error(message));
          setRows([]);
          setRowCount(0);
          return;
        }
        setRows(result?.data ?? []);
        setRowCount(result?.rowCount ?? result?.data?.length ?? 0);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setRows([]);
          setRowCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryKey, argsKey, reloadCounter]);

  return {
    rows,
    rowCount,
    isLoading,
    error,
    refetch: () => setReloadCounter((c) => c + 1),
  };
}

/**
 * Column names come back from the API however SQL Server cased them, and QueryColumns metadata
 * lowercases some of them. This resolves a value case-insensitively so pages aren't brittle to that.
 */
export function field(row, name, fallback = null) {
  if (!row) return fallback;
  if (row[name] !== undefined) return row[name];
  const key = Object.keys(row).find((k) => k.toLowerCase() === String(name).toLowerCase());
  return key === undefined ? fallback : row[key];
}

/** Coerce a NexusQuery value to a number — numerics can arrive as strings depending on column type. */
export function num(row, name, fallback = 0) {
  const v = field(row, name, null);
  if (v === null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
